import axios from 'axios';
import {
  QUESTIONS_REQUEST,
  QUESTIONS_SUCCESS,
  QUESTIONS_APPEND,
  QUESTIONS_LOADING_MORE,
  QUESTIONS_FAIL,
  VOTE_REQUEST,
  VOTE_SUCCESS,
  VOTE_FAIL,
  COMMENTS_REQUEST,
  COMMENTS_SUCCESS,
  COMMENTS_FAIL,
  COMMENT_CREATE_SUCCESS,
  COMMENT_CREATE_FAIL,
  COMMENT_LIKE_SUCCESS,
  COMMENT_REPLY_SUCCESS,
  QUESTION_SINGLE_SUCCESS,
  QUESTION_CREATE_REQUEST,
  QUESTION_CREATE_SUCCESS,
  QUESTION_CREATE_FAIL,
  QUESTION_DELETE_SUCCESS,
  COMMENT_DELETE_SUCCESS,
  FOLLOW_TOGGLE_SUCCESS,
  QUESTION_PATCH,
} from '../types/game';
import { DOMAIN } from '../constants';
import getDeviceId from '../../utils/deviceId';
import { pushStreakSnapshot } from '../../hooks/useStreak';
import buildAuthHeaders from '../../utils/apiHeaders';
import { readAttribution } from '../../utils/attribution';

// Single source of truth for auth headers is utils/apiHeaders.js - this
// thin adapter keeps the (getState)-based signature the 20+ thunks below
// already use. A change to token handling lands in ONE file now.
const authConfig = (getState) => buildAuthHeaders(getState().userLogin?.userInfo);

export const fetchCategories = () => async (_, getState) => {
  try {
    const config = authConfig(getState);
    const { data } = await axios.get(`${DOMAIN}/api/v1/engagement/categories/`, config);
    return data;
  } catch (error) {
    console.error('Failed to fetch categories:', error);
    return [];
  }
};

export const fetchQuestions = (category, feed) => async (dispatch, getState) => {
  // Request token guards against the slow-response race: tap "Sports" then
  // quickly "Food" - if the Sports response lands second, it used to merge
  // into the Food feed (cross-category union) and reset pagination. The
  // reducer only accepts the SUCCESS whose requestId matches the latest
  // REQUEST it saw.
  const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  try {
    dispatch({ type: QUESTIONS_REQUEST, meta: { requestId } });
    const config = authConfig(getState);
    const categoryParam = category ? `&category=${category}` : '';
    const feedParam = feed ? `&feed=${feed}` : '';
    const seed = Date.now();
    const seedParam = `&seed=${seed}`;
    const { data } = await axios.get(`${DOMAIN}/api/v1/game/questions/?page=1&limit=10${categoryParam}${feedParam}${seedParam}`, config);
    dispatch({ type: QUESTIONS_SUCCESS, payload: { questions: data, seed }, meta: { requestId } });
  } catch (error) {
    dispatch({
      type: QUESTIONS_FAIL,
      payload: error.response && error.response.data.detail ? error.response.data.detail : error.message,
      meta: { requestId },
    });
  }
};

export const fetchQuestion = (questionId) => async (dispatch, getState) => {
  try {
    const config = authConfig(getState);
    const { data } = await axios.get(`${DOMAIN}/api/v1/game/questions/${questionId}`, config);
    dispatch({ type: QUESTION_SINGLE_SUCCESS, payload: data });
    return data;
  } catch (error) {
    // Don't dispatch QUESTIONS_FAIL — this is a single question fetch, not a feed error.
    // VotePage handles the missing question case via its loading/null checks.
    console.error('Fetch question failed:', error?.response?.data?.detail || error.message);
    return null;
  }
};

export const fetchComparison = (slug) => async (_dispatch, getState) => {
  try {
    const config = authConfig(getState);
    const { data } = await axios.get(`${DOMAIN}/api/v1/game/comparison/${slug}`, config);
    // Deliberately NOT dispatched into the questions store: the endpoint
    // returns a raw camelCase Prisma row (optionALabel, _count, no
    // percent_a/user_vote), and merging it into the snake_case feed slice
    // permanently broke that question's card. The lone caller
    // (ComparisonPage) only needs the id + labels to build the redirect;
    // the /v/:id page then fetches the properly formatted question.
    return data;
  } catch (error) {
    console.error('Fetch comparison failed:', error?.response?.data?.detail || error.message);
    return null;
  }
};

export const fetchMoreQuestions = (category, feed, author) => async (dispatch, getState) => {
  // Read outside the try so the catch can tag QUESTIONS_FAIL with the same
  // generation token: an untagged FAIL bypasses the reducer's stale-guard and
  // would clobber a fresh in-flight load with loading:false + error.
  const { questionsFeed: { page, hasMore, loadingMore, seed, activeRequestId } } = getState();
  if (!hasMore || loadingMore) return;
  try {
    dispatch({ type: QUESTIONS_LOADING_MORE });
    const config = authConfig(getState);
    const nextPage = page + 1;
    const categoryParam = category ? `&category=${category}` : '';
    const feedParam = feed ? `&feed=${feed}` : '';
    // Author scope rides with feed=author; encoded because usernames are
    // user-supplied even though the validator restricts the charset.
    const authorParam = author ? `&author=${encodeURIComponent(author)}` : '';
    const currentSeed = seed || Date.now();
    const seedParam = `&seed=${currentSeed}`;
    const { data } = await axios.get(`${DOMAIN}/api/v1/game/questions/?page=${nextPage}&limit=10${categoryParam}${feedParam}${authorParam}${seedParam}`, config);
    // Tag the append with the feed generation it belongs to: if a fresh
    // QUESTIONS_REQUEST (or a LOGIN/REGISTER reset) happened while this
    // page was in flight, the reducer drops the stale append instead of
    // injecting old-fetch rows and clobbering seed/page/hasMore.
    dispatch({ type: QUESTIONS_APPEND, payload: { questions: data, seed: currentSeed }, meta: { requestId: activeRequestId } });
  } catch (error) {
    dispatch({
      type: QUESTIONS_FAIL,
      payload: error.response && error.response.data.detail ? error.response.data.detail : error.message,
      meta: { requestId: activeRequestId },
    });
  }
};

export const submitVote = (questionId, choice, source) => async (dispatch, getState) => {
  try {
    dispatch({ type: VOTE_REQUEST });
    const config = authConfig(getState);
    const { data } = await axios.post(`${DOMAIN}/api/v1/game/questions/${questionId}/vote/`, { choice, source }, config);
    dispatch({ type: VOTE_SUCCESS, payload: data });
    // The vote response carries the freshest streak state; write it through
    // to the shared streak store so the navbar/pill/reveal all update at
    // once instead of the reveal reading a parallel `question.streak` truth.
    if (data && data.streak) pushStreakSnapshot(data.streak);
    return data;
  } catch (error) {
    const status = error.response?.status;
    dispatch({
      type: VOTE_FAIL,
      payload: error.response && error.response.data.detail ? error.response.data.detail : error.message,
    });
    // Backend returns 404 when the target question was soft-deleted between
    // load and vote. Purge from Redux so the VotePage can't keep showing it,
    // and surface the reason so the caller can route the user away.
    if (status === 404) {
      dispatch({ type: QUESTION_DELETE_SUCCESS, payload: questionId });
      return { gone: true };
    }
    // 409 = the author closed voting between load and submit. Reporting this
    // as a connection problem made users retry a vote that can never land.
    if (status === 409) {
      dispatch({ type: QUESTION_PATCH, payload: { id: questionId, patch: { is_closed: true } } });
      return { closed: true };
    }
    return null;
  }
};

export const fetchComments = (questionId) => async (dispatch, getState) => {
  try {
    dispatch({ type: COMMENTS_REQUEST });
    const config = authConfig(getState);
    const { data } = await axios.get(`${DOMAIN}/api/v1/game/questions/${questionId}/comments/`, config);
    dispatch({ type: COMMENTS_SUCCESS, payload: data });
  } catch (error) {
    dispatch({
      type: COMMENTS_FAIL,
      payload: error.response && error.response.data.detail ? error.response.data.detail : error.message,
    });
  }
};

export const createComment = (questionId, text) => async (dispatch, getState) => {
  try {
    const config = authConfig(getState);
    const { data } = await axios.post(
      `${DOMAIN}/api/v1/game/questions/${questionId}/comments/`,
      { text },
      config
    );
    dispatch({ type: COMMENT_CREATE_SUCCESS, payload: data });
    dispatch(fetchComments(questionId));
    return data;
  } catch (error) {
    const message = error.response?.data?.detail || error.message || 'Failed to post comment';
    dispatch({ type: COMMENT_CREATE_FAIL, payload: message });
    return null;
  }
};

export const likeComment = (questionId, commentId, parentId) => async (dispatch, getState) => {
  // The comments slice is registered as `questionComments` in store.js.
  // This previously destructured a nonexistent `state.game`, which threw
  // synchronously and turned every comment-like into a silent no-op (the
  // 80 lines of optimistic-update code below were unreachable).
  const { questionComments: { comments } } = getState();
  
  let targetComment = null;
  if (parentId) {
    const parent = (comments || []).find(c => c.id === parentId);
    if (parent && parent.replies) {
      targetComment = parent.replies.find(r => r.id === commentId);
    }
  } else {
    targetComment = (comments || []).find(c => c.id === commentId);
  }

  if (!targetComment) {
    try {
      const config = authConfig(getState);
      const { data } = await axios.post(
        `${DOMAIN}/api/v1/game/questions/${questionId}/comments/${commentId}/like/`,
        {},
        config
      );
      dispatch({ type: COMMENT_LIKE_SUCCESS, payload: { commentId, parentId, ...data } });
      return data;
    } catch (error) {
      console.error('Like failed:', error.response?.data?.detail || error.message);
      return null;
    }
  }

  const previouslyLiked = !!targetComment.is_liked;
  const previousLikesCount = targetComment.likes_count || 0;

  const optimisticLiked = !previouslyLiked;
  const optimisticLikesCount = optimisticLiked ? previousLikesCount + 1 : Math.max(0, previousLikesCount - 1);

  dispatch({
    type: COMMENT_LIKE_SUCCESS,
    payload: {
      commentId,
      parentId,
      liked: optimisticLiked,
      likes_count: optimisticLikesCount
    }
  });

  try {
    const config = authConfig(getState);
    const { data } = await axios.post(
      `${DOMAIN}/api/v1/game/questions/${questionId}/comments/${commentId}/like/`,
      {},
      config
    );
    dispatch({ type: COMMENT_LIKE_SUCCESS, payload: { commentId, parentId, ...data } });
    return data;
  } catch (error) {
    console.error('Like failed, rolling back:', error.response?.data?.detail || error.message);
    dispatch({
      type: COMMENT_LIKE_SUCCESS,
      payload: {
        commentId,
        parentId,
        liked: previouslyLiked,
        likes_count: previousLikesCount
      }
    });
    return null;
  }
};

export const createQuestion = (title, optionALabel, optionBLabel, optionAImage, optionBImage, categoryId, cropA, cropB, optionType = 'image', optionAText = '', optionBText = '', audience = 'public', inviteList = [], correctOption = null) => async (dispatch, getState) => {
  try {
    dispatch({ type: QUESTION_CREATE_REQUEST });

    const {
      userLogin: { userInfo },
    } = getState();

    const formData = new FormData();
    formData.append('title', title);
    formData.append('option_a_label', optionALabel);
    formData.append('option_b_label', optionBLabel);
    if (correctOption) {
      formData.append('correct_option', correctOption);
    }
    if (optionType === 'text') {
      formData.append('option_a_type', 'text');
      formData.append('option_b_type', 'text');
      formData.append('option_a_text', optionAText);
      formData.append('option_b_text', optionBText);
    } else {
      formData.append('option_a_image', optionAImage);
      formData.append('option_b_image', optionBImage);
      if (cropA) formData.append('crop_a', JSON.stringify(cropA));
      if (cropB) formData.append('crop_b', JSON.stringify(cropB));
    }
    if (categoryId) formData.append('category', categoryId);
    // PR 2.2 — audience + invite list. Default 'public' so existing
    // call sites keep posting public rows.
    formData.append('audience', audience);
    if (audience === 'invite_only' && Array.isArray(inviteList) && inviteList.length > 0) {
      formData.append('invite_list', JSON.stringify(inviteList));
    }

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
        'X-Device-Id': getDeviceId(),
        ...(userInfo && userInfo.access ? { Authorization: `Bearer ${userInfo.access}` } : {}),
      },
    };

    const { data } = await axios.post(`${DOMAIN}/api/v1/game/questions/`, formData, config);
    dispatch({ type: QUESTION_CREATE_SUCCESS, payload: data });
    return data;
  } catch (error) {
    dispatch({
      type: QUESTION_CREATE_FAIL,
      payload: error.response && error.response.data.detail ? error.response.data.detail : error.message,
    });
    throw error;
  }
};

export const toggleBookmark = (questionId) => async (dispatch, getState) => {
  try {
    const config = authConfig(getState);
    const { data } = await axios.post(`${DOMAIN}/api/v1/engagement/bookmarks/${questionId}/`, {}, config);
    const { questionsFeed: { questions } } = getState();
    const current = questions.find((q) => q.id === questionId);
    if (current) {
      const prevCount = Number(current.bookmark_count) || 0;
      const delta = data.bookmarked ? 1 : -1;
      dispatch({
        type: QUESTION_PATCH,
        payload: {
          id: questionId,
          patch: {
            is_bookmarked: !!data.bookmarked,
            bookmark_count: Math.max(0, prevCount + delta),
          },
        },
      });
    }
    return data;
  } catch (error) {
    console.error('Bookmark toggle failed:', error);
    throw error;
  }
};

export const fetchBookmarks = () => async (dispatch, getState) => {
  try {
    const config = authConfig(getState);
    const { data } = await axios.get(`${DOMAIN}/api/v1/engagement/bookmarks/`, config);
    return data;
  } catch (error) {
    const message = error.response?.data?.detail || error.message || 'Failed to load bookmarks';
    throw new Error(message);
  }
};

export const replyToComment = (questionId, commentId, text) => async (dispatch, getState) => {
  if (!text) return null;
  try {
    const config = authConfig(getState);
    const { data } = await axios.post(
      `${DOMAIN}/api/v1/game/questions/${questionId}/comments/${commentId}/reply/`,
      { text },
      config
    );
    dispatch({ type: COMMENT_REPLY_SUCCESS, payload: { parentId: commentId, reply: data } });
    dispatch(fetchComments(questionId));
    return data;
  } catch (error) {
    console.error('Reply failed:', error);
    return null;
  }
};

export const deleteQuestion = (questionId) => async (dispatch, getState) => {
  try {
    const config = authConfig(getState);
    await axios.delete(`${DOMAIN}/api/v1/game/questions/${questionId}/`, config);
    dispatch({ type: QUESTION_DELETE_SUCCESS, payload: questionId });
  } catch (error) {
    console.error('Delete question failed:', error);
    throw error;
  }
};

export const toggleFollow = (username) => async (dispatch, getState) => {
  try {
    const config = authConfig(getState);
    const { data } = await axios.post(`${DOMAIN}/api/v1/engagement/follow/${username}/`, {}, config);
    dispatch({ type: FOLLOW_TOGGLE_SUCCESS, payload: { username, following: data.following } });
    return data;
  } catch (error) {
    console.error('Follow toggle failed:', error);
    throw error;
  }
};

// `params` is an already-encoded query string ("q=al&filter=mutuals").
// Both filters are applied server-side, BEFORE pagination, so X-Total-Count
// stays consistent with the rows returned - filtering client-side would show a
// short page against a full count and break infinite-scroll math.
export const fetchFollowers = (username, params = '') => async (_, getState) => {
  try {
    const config = authConfig(getState);
    const qs = params ? `?${params}` : '';
    const { data } = await axios.get(`${DOMAIN}/api/v1/engagement/followers/${username}/${qs}`, config);
    return data;
  } catch (error) {
    console.error('Fetch followers failed:', error);
    return [];
  }
};

export const fetchFollowing = (username, params = '') => async (_, getState) => {
  try {
    const config = authConfig(getState);
    const qs = params ? `?${params}` : '';
    const { data } = await axios.get(`${DOMAIN}/api/v1/engagement/following/${username}/${qs}`, config);
    return data;
  } catch (error) {
    console.error('Fetch following failed:', error);
    return [];
  }
};

export const deleteComment = (questionId, commentId, parentId) => async (dispatch, getState) => {
  try {
    const config = authConfig(getState);
    await axios.delete(`${DOMAIN}/api/v1/game/questions/${questionId}/comments/${commentId}/`, config);
    dispatch({ type: COMMENT_DELETE_SUCCESS, payload: { commentId, parentId } });
  } catch (error) {
    console.error('Delete comment failed:', error);
    throw error;
  }
};

// Asker-only: close voting on a question. Idempotent on the backend, so a
// double-tap is safe. Optimistically marks the question closed in Redux so
// the UI flips immediately; reconciles to server timestamp on success.
export const closeQuestion = (questionId) => async (dispatch, getState) => {
  try {
    const config = authConfig(getState);
    const { data } = await axios.post(
      `${DOMAIN}/api/v1/game/questions/${questionId}/close/`,
      {},
      config,
    );
    dispatch({
      type: QUESTION_PATCH,
      payload: {
        id: questionId,
        patch: {
          is_closed: true,
          closed_at: data?.closed_at || new Date().toISOString(),
        },
      },
    });
    return data;
  } catch (error) {
    console.error('Close question failed:', error?.response?.data?.detail || error.message);
    throw error;
  }
};

// Asker-only: reopen a previously closed question. Idempotent on the backend.
export const reopenQuestion = (questionId) => async (dispatch, getState) => {
  try {
    const config = authConfig(getState);
    const { data } = await axios.post(
      `${DOMAIN}/api/v1/game/questions/${questionId}/reopen/`,
      {},
      config,
    );
    dispatch({
      type: QUESTION_PATCH,
      payload: {
        id: questionId,
        patch: {
          is_closed: false,
          closed_at: null,
        },
      },
    });
    return data;
  } catch (error) {
    console.error('Reopen question failed:', error?.response?.data?.detail || error.message);
    throw error;
  }
};

// Fire-and-forget analytics ping for result-card shares. Distinct from
// recordShare — this counts how often the static 1080×1920 PNG flows out
// (vs. the live OG/share-card path). Errors are swallowed; the user-visible
// share already succeeded by the time this fires.
export const recordResultCardShare = async (questionId, platform = 'other') => {
  if (!questionId) return;
  try {
    await fetch(`${DOMAIN}/api/v1/game/questions/${questionId}/result-card-shared/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Device-Id': getDeviceId(),
      },
      body: JSON.stringify({ platform }),
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('result-card share record failed', err);
  }
};

// Record a share AFTER the user commits (picks a platform / clicks "post").
// The `platform` param drives per-platform analytics. If omitted, backend
// records it as "other". Also forwards inbound-attribution context so we
// can measure chains (e.g. "inbound from twitter → re-shared on whatsapp").
export const recordShare = (questionId, platform = 'other') => async (dispatch, getState) => {
  const attribution = readAttribution();
  const {
    questionsFeed: { questions },
  } = getState();
  const current = questions.find((q) => q.id === questionId);
  const prevCount = Number(current?.share_count) || 0;
  dispatch({
    type: QUESTION_PATCH,
    payload: { id: questionId, patch: { share_count: prevCount + 1 } },
  });
  try {
    const config = authConfig(getState);
    const { data } = await axios.post(
      `${DOMAIN}/api/v1/game/questions/${questionId}/share/`,
      {
        platform,
        inbound_source: attribution?.source || null,
        inbound_campaign: attribution?.campaign || null,
      },
      config,
    );
    if (typeof data?.share_count === 'number') {
      dispatch({
        type: QUESTION_PATCH,
        payload: { id: questionId, patch: { share_count: data.share_count } },
      });
    }
  } catch {
    // revert optimistic bump
    dispatch({
      type: QUESTION_PATCH,
      payload: { id: questionId, patch: { share_count: prevCount } },
    });
  }
};
